'use client';

import { useQuery } from '@tanstack/react-query';
import { provenanceKeys, type FileSummary } from '@/shared/cors-provenance-api';
import { getFileInfoFromServer } from '@/shared/functions';
import { getCurrentFileInfo } from '@/shared/clientFunctions';
import { useProvenanceSession } from './useProvenanceSession';

/**
 * CM-safe (obfuscated) preview URL from a resolved file-info record.
 *
 * Deliberately reads ``previewUrl`` ONLY — the server returns the obfuscated
 * transcode there. It must never fall through to ``fileUrl`` / ``downloadUrl``,
 * which point at the original (un-obfuscated) asset. Mirrors the ``obfUrl``
 * resolution in the citations / Evidence Locker flow (answer-item.tsx).
 */
export function obfuscatedUrlFromFileInfo(
  info: FileSummary | undefined | null,
): string | null {
  return info?.previewUrl ?? null;
}

/**
 * Original-image URL from a file-info record fetched with ``bypass_cm=true``.
 * Prefers the web-friendly ``previewUrl`` transcode, falling back to the raw
 * ``fileUrl``. Mirrors the original-view resolution in answer-item.tsx
 * (``info?.previewUrl ?? info?.fileUrl``).
 */
export function originalUrlFromFileInfo(
  info: FileSummary | undefined | null,
): string | null {
  return info?.previewUrl ?? info?.fileUrl ?? null;
}

/**
 * URL for the finding's *default (safe) view*, resolved the same way the rest of
 * the app treats a content-moderated file — see the ``cmType`` gate in
 * answer-item.tsx (``cm?.type && cm.findingStatus !== 'REVIEWED'``):
 *
 * - Still actively moderated (``contentModeration.type`` set and NOT
 *   ``REVIEWED``) → return ONLY the CM-safe obfuscated ``previewUrl`` (via
 *   {@link obfuscatedUrlFromFileInfo}); never fall through to the original.
 * - Cleared / reviewed / not moderated → the file behaves like any other source,
 *   so return the real image (``previewUrl ?? fileUrl``, via
 *   {@link originalUrlFromFileInfo}). This is why a REVIEWED finding whose
 *   obfuscated transcode has been dropped shows the actual image instead of a
 *   "restricted" placeholder, matching how answer-item / unifiedSources render a
 *   reviewed source.
 *
 * Expects a record fetched WITHOUT ``bypass_cm`` (the CM-safe response), so a
 * still-moderated file only ever exposes its obfuscated ``previewUrl`` here.
 */
export function safeViewUrlFromFileInfo(
  info: FileSummary | undefined | null,
): string | null {
  const cm = info?.contentModeration;
  const stillModerated = Boolean(cm?.type) && cm?.findingStatus !== 'REVIEWED';
  return stillModerated ? obfuscatedUrlFromFileInfo(info) : originalUrlFromFileInfo(info);
}

/**
 * Resolve a content-moderation finding's preview through the same shared
 * provenance helpers the Evidence Locker and Sources previews use, so path
 * normalization (``toProvenanceLookupPath``) and obfuscation semantics stay
 * consistent across the app.
 *
 * - ``bypassCm: false`` → cached/deduped ``getCurrentFileInfo``; use its
 *   ``previewUrl`` (via {@link obfuscatedUrlFromFileInfo}) as the CM-safe
 *   obfuscated preview when the finding has no direct ``obfuscatedUrl``.
 * - ``bypassCm: true`` → uncached ``getFileInfoFromServer`` so the audited
 *   ``bypass_cm=true`` reveal is recorded server-side every time a csam-reviewer
 *   switches to the original view. The query is not cached (``staleTime`` /
 *   ``gcTime`` of 0) to preserve that audit-on-every-reveal behavior.
 *   Pass the finding's obfuscated asset ``s3://`` URL (not its searchable
 *   ``filePath``) so the lookup hits the real processed object; a bare
 *   searchable path would get a ``data/bulk/`` prefix and 404.
 *
 * Stays disabled until the caller opts in (``enabled``).
 */
export function useFindingFileInfoPreview(
  s3Url: string | null | undefined,
  opts: { bypassCm: boolean; enabled: boolean },
) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const { bypassCm, enabled } = opts;

  return useQuery<FileSummary | null, Error>({
    queryKey: [
      ...provenanceKeys.fileInfoForUrl(cellId, s3Url ?? ''),
      bypassCm ? 'bypass_cm' : 'cm',
    ],
    queryFn: () =>
      bypassCm
        ? getFileInfoFromServer(s3Url as string, cellId, accessToken, true)
        : getCurrentFileInfo(s3Url as string, cellId, accessToken),
    enabled: sessionEnabled && Boolean(s3Url) && enabled,
    staleTime: bypassCm ? 0 : 60 * 1000,
    gcTime: bypassCm ? 0 : undefined,
  });
}
